import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { isThemeFlagOn } from "../gameSetupStorage";
import {
  getDvSourceImageUrl,
  themeBackgroundKeyForViewport,
  toCssUrlValue,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  saveSnakesLaddersAnswerProgress,
  setStageActive,
} from "../../functions/stageReportSync";
import { fetchQuizGameQuestions, shuffleCopy } from "./quizGameUtils";
import "./dragdrop.css";
import "./quizgame.css";

const STAGE_NUM = 1;
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const OPTION_COLORS = ["#f59e0b", "#2563eb", "#7c3aed", "#14b8a6", "#ec4899", "#ef4444"];

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
  ));
}

function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData, currentTheme } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sid = String(storedUser.sessionId || "");
    return (
      sid === "demobypass" || role === "demobypass" || source === "DEMO" || sid.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const shuffleEnabled = isThemeFlagOn(themeData?.shuffle, false);
  const revealAnswer = isThemeFlagOn(themeData?.question_table, false);
  const pointsPerCorrect = Math.max(
    1,
    parseInt(themeData?.correctPoints ?? themeData?.points, 10) || 1
  );
  const configuredCount = parseInt(themeData?.noOfQuestion, 10);

  const titleColor = String(themeData?.landing_page_title_color || "#111").trim();
  const boxColor = String(themeData?.questionBox_color || "#fbf99f").trim();
  const buttonTextColor = String(themeData?.button_Textcolor || "#ffffff").trim();
  const gameContent = Array.isArray(themeData?.game_content)
    ? themeData.game_content.map((v) => String(v ?? "").trim()).filter(Boolean)
    : [];

  const backgroundUrl = useMemo(() => {
    const key = themeBackgroundKeyForViewport(themeData);
    return key ? toCssUrlValue(getDvSourceImageUrl(key)) : "none";
  }, [themeData]);

  const [allQuestions, setAllQuestions] = useState(null); // null = not loaded yet
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [score, setScore] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [timerOn, setTimerOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const qIndexRef = useRef(0);
  const scoreRef = useRef(0);
  const elapsedRef = useRef(0);
  const answersRef = useRef([]);
  const eqCounterRef = useRef(0);
  const finished = useRef(false);
  const startedRef = useRef(false);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  // Unlock document scroll for the stage (login/rules CSS locks body overflow on mobile portrait).
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("dd-stage-open");
    html.classList.add("dd-stage-open");

    const apply = () => {
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");
      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("dd-stage-open");
      html.classList.remove("dd-stage-open");
      ["overflow", "overflow-x", "overflow-y", "height", "max-height"].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);

  // Fetch questions from the `questions` SQL table (old/game.php's SELECT * FROM questions).
  useEffect(() => {
    let cancelled = false;
    async function loadQuestions() {
      // `currentTheme` is the theme's real slug/id (themesdata's `themename` key) —
      // themeData.themeName is only the human-readable display name, not the key
      // the `questions` table is scoped by.
      if (!currentTheme || !adminToken) return;
      try {
        const rows = await fetchQuizGameQuestions({
          themeName: currentTheme,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          token: adminToken,
        });
        if (!cancelled) setAllQuestions(rows);
      } catch (err) {
        console.error("Game loadQuestions:", err);
        if (!cancelled) setAllQuestions([]);
      }
    }
    loadQuestions();
    return () => {
      cancelled = true;
    };
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setStageActive(STAGE_NUM, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        const st = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {});
          if (redirect && redirect !== "/stage1" && redirect !== "/game") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error("Game bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, storedUser]);

  // No questions configured at all — match old/game.php's "no questions" case.
  useEffect(() => {
    if (themeStatus === "loading" || !dbReady || allQuestions === null) return;
    if (allQuestions.length > 0) return;
    Swal.fire({
      icon: "warning",
      title: "No Questions Found",
      text: "Please add questions from Admin before continuing.",
      confirmButtonText: "OK",
    }).then(() => navigate("/rules", { replace: true }));
  }, [allQuestions, themeStatus, dbReady, navigate]);

  // Admin's configured question count exceeds what's uploaded — match old/game.php's guard.
  useEffect(() => {
    if (!dbReady || allQuestions === null || !allQuestions.length) return;
    if (!(Number.isFinite(configuredCount) && configuredCount > allQuestions.length)) return;
    Swal.fire({
      icon: "warning",
      title: "",
      text: "The number of questions shown is less than what's set by the admin. The game will begin once the questions are updated.",
      confirmButtonText: "OK",
    }).then(() => navigate("/rules", { replace: true }));
  }, [dbReady, allQuestions, configuredCount, navigate]);

  // Build the working (shuffled + sliced) question list once questions are known.
  useEffect(() => {
    if (allQuestions === null || !allQuestions.length) return;
    if (Number.isFinite(configuredCount) && configuredCount > allQuestions.length) return;
    const targetCount =
      Number.isFinite(configuredCount) && configuredCount > 0
        ? Math.min(configuredCount, allQuestions.length)
        : allQuestions.length;
    const source = shuffleEnabled ? shuffleCopy(allQuestions) : allQuestions;
    setQuestions(source.slice(0, targetCount));
  }, [allQuestions, shuffleEnabled, configuredCount]);

  useEffect(() => {
    if (startedRef.current || !dbReady || !questions.length) return;
    startedRef.current = true;
    setTimerOn(true);
  }, [dbReady, questions.length]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const currentQuestion = questions[qIndex] || null;

  const persistAndLeave = useCallback(
    async (finalScore) => {
      const finalTime = formatClock(elapsedRef.current);
      setTimerOn(false);
      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
            stageNumber: STAGE_NUM,
            score: finalScore,
            total_score: finalScore,
            time: finalTime,
            gameover: true,
            winner: "completed",
            user_answers: answersRef.current,
          });
          if (saveResult?.stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
            setStageRowId(String(saveResult.stageId));
          }
        }
      } catch (err) {
        console.error("Game finish:", err);
      }
      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      if (isDemoBypass) {
        Swal.fire({
          icon: "info",
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          window.location.assign(DEMO_PLANS_URL);
        });
        return;
      }
      navigate("/thankyou", {
        replace: true,
        state: { points: finalScore, time: finalTime },
      });
    },
    [adminToken, backendBase, isDemoBypass, navigate, stageRowId, storedUser]
  );

  const saveProgressAfterAnswer = useCallback(
    async (pointsThisAnswer) => {
      if (isDemoBypass || !backendBase || !adminToken) return;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId) return;
      try {
        await saveSnakesLaddersAnswerProgress({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          answerIndex: Math.max(1, Math.min(eqCounterRef.current, 13)),
          pointsThisAnswer,
          totalScore: scoreRef.current,
          time: formatClock(elapsedRef.current),
          userAnswers: answersRef.current,
        });
      } catch (err) {
        console.error("saveProgressAfterAnswer:", err);
      }
    },
    [adminToken, backendBase, isDemoBypass, stageRowId, storedUser]
  );

  const advance = useCallback(() => {
    const next = qIndexRef.current + 1;
    setSelectedIndex(null);
    if (isDemoBypass || next >= questions.length) {
      finished.current = true;
      persistAndLeave(scoreRef.current);
      return;
    }
    qIndexRef.current = next;
    setQIndex(next);
    setBusy(false);
  }, [questions.length, persistAndLeave, isDemoBypass]);

  const handleSubmit = useCallback(async () => {
    if (!currentQuestion || busy || finished.current) return;

    if (selectedIndex == null) {
      Swal.fire({
        html: `
          <div class="qg-feedback">
            <h3 class="qg-feedback__title">Hold on!</h3>
            <p class="qg-feedback__text">Please select an option to continue.</p>
          </div>
        `,
        confirmButtonText: "OK",
        showClass: { popup: "qg-feedback-popup--in" },
        buttonsStyling: false,
        customClass: {
          popup: "qg-feedback-popup qg-feedback-popup--warning",
          confirmButton: "swal2-confirm qg-feedback__ok",
        },
      });
      return;
    }

    setBusy(true);
    const correct = selectedIndex === currentQuestion.correctIndex;

    eqCounterRef.current += 1;
    answersRef.current.push({
      questionId: currentQuestion.id,
      question: currentQuestion.question_name,
      user_answer: currentQuestion.options[selectedIndex - 1],
      correct_answer: currentQuestion.correctAnswer,
      is_correct: correct ? 1 : 0,
      questionNumber: eqCounterRef.current,
      answeredAt: new Date().toISOString(),
    });

    if (correct) {
      scoreRef.current += pointsPerCorrect;
      setScore(scoreRef.current);
      advance();
      saveProgressAfterAnswer(pointsPerCorrect);
      return;
    }

    if (revealAnswer) {
      await Swal.fire({
        html: `
          <div class="qg-feedback">
            <h3 class="qg-feedback__title">Incorrect!</h3>
            <p class="qg-feedback__text">The correct answer is: <strong>${escapeHtml(currentQuestion.correctAnswer)}</strong></p>
          </div>
        `,
        confirmButtonText: "OK",
        showClass: { popup: "qg-feedback-popup--in" },
        buttonsStyling: false,
        customClass: {
          popup: "qg-feedback-popup qg-feedback-popup--wrong",
          confirmButton: "swal2-confirm qg-feedback__ok",
        },
      });
    }
    advance();
    saveProgressAfterAnswer(0);
  }, [currentQuestion, busy, selectedIndex, pointsPerCorrect, revealAnswer, advance, saveProgressAfterAnswer]);

  const stillPreparing =
    allQuestions !== null &&
    allQuestions.length > 0 &&
    !(Number.isFinite(configuredCount) && configuredCount > allQuestions.length) &&
    questions.length === 0 &&
    !finished.current;

  if (
    (themeStatus === "loading" && !themeData) ||
    !dbReady ||
    allQuestions === null ||
    stillPreparing
  ) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const pointsLabel = String(Math.max(0, Math.floor(score))).padStart(2, "0");
  const hintUrl = currentQuestion?.question_hint
    ? getDvSourceImageUrl(currentQuestion.question_hint)
    : "";

  return (
    <div className="dd-page qg-page" style={{ backgroundImage: backgroundUrl }}>
      <div className="dd-hud-bar">
        <div className="dd-hud">
          <div
            className="dd-hud-pill dd-hud-pill--timer"
            style={{ background: boxColor, color: buttonTextColor }}
            aria-label={`Time ${formatClock(elapsedSec)}`}
          >
            <span className="dd-hud-icon" aria-hidden>
              <svg viewBox="0 0 40 44" width="36" height="40">
                <rect x="8" y="2" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                <rect x="8" y="37" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                <path
                  d="M11 7h18l-7.5 13.5L29 34H11l7.5-13.5L11 7z"
                  fill="#fff"
                  stroke="#3a3a3a"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
                <path d="M14 9.5h12l-4.2 7.6H18.2L14 9.5z" fill="url(#qgHourSandTop)" />
                <path d="M18.2 25.8h3.6L26 32.5H14l4.2-6.7z" fill="url(#qgHourSandBot)" />
                <circle cx="20" cy="21" r="1.1" fill="#f5c842" />
                <circle cx="18.6" cy="23.2" r="0.8" fill="#f0a020" />
                <circle cx="21.4" cy="23.8" r="0.7" fill="#f5c842" />
                <defs>
                  <linearGradient id="qgHourSandTop" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffe27a" />
                    <stop offset="100%" stopColor="#f0a020" />
                  </linearGradient>
                  <linearGradient id="qgHourSandBot" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f5c842" />
                    <stop offset="100%" stopColor="#e08a10" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="dd-hud-value">{formatClock(elapsedSec)}</span>
          </div>
        </div>

        <div className="dd-hud">
          <div
            className="dd-hud-pill dd-hud-pill--points"
            style={{ background: boxColor, color: buttonTextColor }}
            aria-label={`Points ${pointsLabel}`}
          >
            <span className="dd-hud-icon" aria-hidden>
              <svg viewBox="0 0 48 48" width="40" height="40">
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="#c47810"
                  transform="translate(0 2.5)"
                />
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="url(#qgStarFill)"
                  stroke="#e8a010"
                  strokeWidth="0.8"
                />
                <defs>
                  <linearGradient id="qgStarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff3a0" />
                    <stop offset="45%" stopColor="#ffd54a" />
                    <stop offset="100%" stopColor="#f0a020" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="dd-hud-value">{pointsLabel}</span>
          </div>
        </div>
      </div>

      {gameContent.length ? (
        <div className="qg-intro-text" style={{ color: titleColor }}>
          {gameContent.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      ) : null}

      {hintUrl ? <img src={hintUrl} alt="" className="qg-hint-image" /> : null}

      {currentQuestion ? (
        <div key={currentQuestion.id} className="qg-question-card">
          <div className="qg-question-wrap">
            <span
              className="qg-question-badge"
              style={{ background: boxColor, color: buttonTextColor }}
              aria-hidden="true"
            >
              ?
            </span>
            <div className="qg-question-pill" style={{ borderColor: boxColor }}>
              <h4 className="qg-question-title">{currentQuestion.question_name}</h4>
            </div>
          </div>

          <div className="qg-options">
            {currentQuestion.options.map((opt, i) => {
              const optionNumber = i + 1;
              const optionColor = OPTION_COLORS[i % OPTION_COLORS.length];
              const isSelected = selectedIndex === optionNumber;
              return (
                <button
                  type="button"
                  key={optionNumber}
                  className={`qg-option${isSelected ? " is-selected" : ""}`}
                  style={
                    isSelected
                      ? { borderColor: optionColor, background: optionColor, color: "#fff" }
                      : { borderColor: optionColor }
                  }
                  onClick={() => setSelectedIndex(optionNumber)}
                  disabled={busy}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <div className="qg-actions">
            <button
              type="button"
              className="qg-submit-btn"
              style={{ color: buttonTextColor, background: boxColor }}
              onClick={handleSubmit}
              disabled={busy}
            >
              {qIndex + 1 >= questions.length ? "SUBMIT" : "NEXT"}
            </button>
            <button
              type="button"
              className="qg-submit-circle"
              style={{ borderColor: boxColor, color: boxColor }}
              aria-label="Next"
              onClick={handleSubmit}
              disabled={busy}
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="dd-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
